import {useMemo,useState} from 'react'
import {Activity,ArrowDownToLine,ChevronDown,ChevronRight,Database,
  RefreshCw,ShieldCheck,AlertTriangle,Filter,Radio,Clock3} from 'lucide-react'
import {useExtensionActivities} from '@/hooks/useApi'
import type {ExtensionActivityEvent,ExtensionActivityFilters} from '@/lib/api'

const sources:Record<string,string>={
 extension_reported:'Chrome reported',
 worker_observed:'OPS Worker observed',
 workflow_observed:'Workflow observed'
}
const outcomes:Record<string,string>={
 success:'text-emerald-400',failure:'text-red-400',
 denied:'text-amber-400',unknown:'text-amber-400',
 attempt:'text-sky-400',pending:'text-text-muted'
}
const field='min-h-[36px] rounded-lg border border-border bg-surface px-2.5 text-xs text-text-primary outline-none focus:border-accent'
const safeCsv=(v:unknown)=>{
 const raw=typeof v==='string'?v:String(v??'')
 const safe=/^[=+\-@\t\r]/.test(raw)?("'"+raw):raw
 return '"'+safe.replace(/"/g,'""')+'"'
}
function downloadCsv(events:ExtensionActivityEvent[]){
 const keys:(keyof ExtensionActivityEvent)[]=[
  'recorded_at','occurred_at','source','action','phase','outcome','reason_code',
  'device_id','browser_session_id','request_id','workflow_id','correlation_id',
  'step_index','duration_ms','frame_age_ms','geometry_available'
 ]
 const rows=[keys.join(','),...events.map(e=>keys.map(k=>safeCsv(e[k])).join(','))]
 const url=URL.createObjectURL(new Blob([rows.join('\r\n')],{type:'text/csv;charset=utf-8'}))
 const a=document.createElement('a')
 a.href=url;a.download='OPS-AD021-Extension-Activities.csv'
 document.body.appendChild(a);a.click();a.remove()
 window.setTimeout(()=>URL.revokeObjectURL(url),500)
}
function ObservationMark({source}:{source:string}){
 const label=sources[source]||'Unknown origin'
 return <span className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] text-text-muted">
  {source==='extension_reported'?<Radio className="h-3 w-3"/>:<ShieldCheck className="h-3 w-3"/>}
  {label}
 </span>
}
function Stage({event}:{event:ExtensionActivityEvent}){
 let details:Record<string,unknown>={}
 try{details=JSON.parse(event.details_json||'{}') as Record<string,unknown>}catch{}
 return <div className="border-l-2 border-border py-3 pl-4">
  <div className="flex flex-wrap items-center gap-2">
   <span className="font-mono text-[11px] text-text-muted">
    {new Date(event.recorded_at).toLocaleTimeString()}
   </span>
   <span className="rounded-md bg-surface-elevated px-2 py-0.5 text-xs font-semibold text-text-primary">
    {event.phase.replaceAll('_',' ')}
   </span>
   <span className={'text-xs font-medium '+(outcomes[event.outcome]||'text-text-muted')}>{event.outcome}</span>
   <ObservationMark source={event.source}/>
  </div>
  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-muted">
   <span>Action: <span className="text-text-primary">{event.action}</span></span>
   {event.reason_code&&<span>Reason: <span className="text-amber-400">{event.reason_code}</span></span>}
   {event.duration_ms!==null&&<span>Duration: {event.duration_ms} ms</span>}
   {event.frame_age_ms!==null&&<span>Frame age: {event.frame_age_ms} ms</span>}
   {event.geometry_available!==null&&
    <span>Trusted geometry: {event.geometry_available===1?'yes':'no'}</span>}
   {event.step_index!==null&&<span>Step: {event.step_index+1}</span>}
   {Object.entries(details).map(([k,v])=>
    <span key={k}>{k}: {typeof v==='string'?v:JSON.stringify(v)}</span>)}
  </div>
  <div className="mt-1 break-all font-mono text-[10px] text-text-muted/70">{event.event_id}</div>
 </div>
}
export function ExtensionActivitiesPanel(){
 const [device,setDevice]=useState('')
 const [action,setAction]=useState('')
 const [phase,setPhase]=useState('')
 const [outcome,setOutcome]=useState('')
 const [source,setSource]=useState('')
 const [correlation,setCorrelation]=useState('')
 const [limit,setLimit]=useState(50)
 const [cursor,setCursor]=useState<{before:string;before_event_id:string}|null>(null)
 const [expanded,setExpanded]=useState<Record<string,boolean>>({})
 const filters:ExtensionActivityFilters={
  device_id:device||undefined,action:action||undefined,phase:phase||undefined,
  outcome:outcome||undefined,source:source||undefined,
  correlation_id:correlation||undefined,limit,
  before:cursor?.before,before_event_id:cursor?.before_event_id
 }
 const {data,isLoading,isFetching,error,refetch}=useExtensionActivities(filters)
 const events=data?.events||[]
 const devices=useMemo(()=>Array.from(new Set(events.map(e=>e.device_id))).sort(),[events])
 const groups=useMemo(()=>{
  const seen=new Map<string,ExtensionActivityEvent[]>()
  for(const e of events){
   // An MCP command is one timeline; a workflow is one larger timeline.
   const key=e.workflow_id?'wf:'+e.workflow_id:
    e.request_id?'req:'+e.request_id:'evt:'+e.event_id
   const group=seen.get(key)||[]
   group.push(e);seen.set(key,group)
  }
  return Array.from(seen.entries()).map(([key,parts])=>({
   key,parts:parts.sort((a,b)=>a.recorded_at.localeCompare(b.recorded_at)),
   current:parts.reduce((v,e)=>e.recorded_at>v.recorded_at?e:v,parts[0]),
  })).sort((a,b)=>b.current.recorded_at.localeCompare(a.current.recorded_at))
 },[events])
 const failures=events.filter(e=>e.outcome==='failure'||e.outcome==='denied').length
 const active=events.filter(e=>e.outcome==='attempt'||e.outcome==='pending').length
 const refresh=()=>{setCursor(null);void refetch()}
 return <section className="space-y-4">
  <header className="rounded-2xl border border-border bg-surface-elevated p-5">
   <div className="flex flex-wrap items-start justify-between gap-3">
    <div>
     <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-accent">OPS / AD-021</div>
     <h2 className="flex items-center gap-2 text-xl font-semibold text-text-primary">
      <Activity className="h-5 w-5"/>Extension Activities
     </h2>
     <p className="mt-2 max-w-2xl text-sm text-text-muted">
      Correlated Chrome, Worker and workflow evidence. A Chrome-reported action is not the same as a verified result.
      Only metadata is stored; no typed text, webpage URLs or screenshots.
     </p>
    </div>
    <div className="flex gap-2">
     <button className={field+' inline-flex items-center gap-1.5'} onClick={refresh}>
      <RefreshCw className={'h-4 w-4 '+(isFetching?'animate-spin':'')}/>Refresh
     </button>
     <button className={field+' inline-flex items-center gap-1.5 disabled:opacity-40'}
      disabled={!events.length} onClick={()=>downloadCsv(events)}>
      <ArrowDownToLine className="h-4 w-4"/>Export visible
     </button>
    </div>
   </div>
  </header>
  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
   {[
    {icon:Database,label:'Loaded events',value:events.length},
    {icon:Activity,label:'Timelines',value:groups.length},
    {icon:AlertTriangle,label:'Failures / denied',value:failures},
    {icon:Clock3,label:'Attempts / pending',value:active}
   ].map(({icon:Icon,label,value})=>
    <div key={label} className="rounded-xl border border-border bg-surface p-3">
     <div className="flex items-center gap-2 text-xs text-text-muted"><Icon className="h-4 w-4"/>{label}</div>
     <div className="mt-1 font-mono text-2xl text-text-primary">{value}</div>
    </div>)}
  </div>
  <div className="rounded-xl border border-border bg-surface p-3">
   <div className="mb-3 flex items-center gap-2 text-sm font-medium text-text-primary">
    <Filter className="h-4 w-4"/>Filters <span className="text-xs font-normal text-text-muted">Server-side; scoped to your authenticated identity</span>
   </div>
   <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
    <select className={field} value={device} aria-label="Device" onChange={e=>{setDevice(e.target.value);setCursor(null)}}>
     <option value="">All owned devices</option>
     {devices.map(d=><option key={d} value={d}>{d}</option>)}
    </select>
    <select className={field} value={outcome} aria-label="Outcome" onChange={e=>{setOutcome(e.target.value);setCursor(null)}}>
     <option value="">All outcomes</option>
     {['success','failure','denied','attempt','unknown','pending'].map(x=><option key={x} value={x}>{x}</option>)}
    </select>
    <select className={field} value={source} aria-label="Source" onChange={e=>{setSource(e.target.value);setCursor(null)}}>
     <option value="">All evidence sources</option>
     {Object.entries(sources).map(([k,v])=><option key={k} value={k}>{v}</option>)}
    </select>
    <select className={field} value={limit} aria-label="Page size" onChange={e=>{setLimit(Number(e.target.value));setCursor(null)}}>
     {[25,50,100].map(n=><option key={n} value={n}>{n} records</option>)}
    </select>
    <input className={field} aria-label="Action" placeholder="Exact action (e.g. virtual_click)"
      value={action} onChange={e=>{setAction(e.target.value);setCursor(null)}}/>
    <input className={field} aria-label="Phase" placeholder="Exact phase (e.g. ack_sent)"
      value={phase} onChange={e=>{setPhase(e.target.value);setCursor(null)}}/>
    <input className={field+' sm:col-span-2'} aria-label="Correlation ID" placeholder="Correlation ID (exact)"
      value={correlation} onChange={e=>{setCorrelation(e.target.value);setCursor(null)}}/>
   </div>
  </div>
  <div className="rounded-xl border border-border bg-surface p-3">
   {isLoading?<div className="p-5 text-sm text-text-muted">Loading activity history…</div>:
    error?<div role="alert" className="p-5 text-sm text-red-400">
     Unable to load authorised Extension Activities. Check the OPS session and the activity endpoint.
    </div>:events.length===0?
     <div className="p-6 text-center text-sm text-text-muted">
      No matching centrally received activity yet. The new extension must be installed and authenticated to upload local events.
     </div>:
     <div className="space-y-2">
      {groups.map(({key,parts,current})=>{
       const open=expanded[key]===true
       const bad=parts.some(e=>e.outcome==='failure'||e.outcome==='denied')
       return <div key={key} className="overflow-hidden rounded-xl border border-border">
        <button onClick={()=>setExpanded(o=>({...o,[key]:!o[key]}))}
         className="flex w-full items-center gap-3 p-3 text-left hover:bg-surface-elevated">
         {open?<ChevronDown className="h-4 w-4 shrink-0 text-text-muted"/>:
          <ChevronRight className="h-4 w-4 shrink-0 text-text-muted"/>}
         <span className={'h-2 w-2 shrink-0 rounded-full '+(bad?'bg-amber-400':'bg-emerald-400')}/>
         <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-text-primary">
           {current.action.replaceAll('_',' ')}
           <span className="ml-2 text-xs font-normal text-text-muted">({parts.length} phases)</span>
          </div>
          <div className="truncate font-mono text-[10px] text-text-muted">{key} • {current.device_id}</div>
         </div>
         <span className="shrink-0 text-[11px] text-text-muted">
          {new Date(current.recorded_at).toLocaleTimeString()}
         </span>
        </button>
        {open&&<div className="border-t border-border px-4 pb-2">
         {parts.map(e=><Stage event={e} key={e.event_id}/>)}
        </div>}
       </div>
      })}
     </div>}
   {data?.has_more&&data.next_before&&data.next_before_id&&
    <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
     <span className="text-xs text-text-muted">Older activity is available.</span>
     <button className={field} onClick={()=>setCursor({before:data.next_before!,before_event_id:data.next_before_id!})}>Older page</button>
    </div>}
   <p className="mt-3 text-[11px] text-text-muted">
    Showing the current filtered page only. Central retention: {data?.retention_days??30} days.
    Client-reported success is never elevated to independently verified.
   </p>
  </div>
 </section>
}
